import { useState, useEffect } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Key, Plus, Folder, ChevronRight, ChevronDown, Lock, Globe, Building2, Clock, EyeOff, Copy, Check, Trash2, FolderInput, Share2, AlertTriangle } from 'lucide-react';
import { vaultAPI, documentationAPI } from '../../services/api';
import { useAuthStore } from '../../context/authStore';
import { useCompanyStore } from '../../context/companyStore';
import CompanyFilter from '../../components/common/CompanyFilter';
import { isInternalRole } from '../../utils/roles.js';
import { notifyError } from '../../context/toastStore';

// Password type badges
const passwordTypes = {
  local_admin: { label: 'Local Admin', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  domain_admin: { label: 'Domain Admin', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  service_account: { label: 'Service Account', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  api_key: { label: 'API Key', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  wifi: { label: 'WiFi', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  other: { label: 'Other', className: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300' },
};

// Visibility badge
function VisibilityBadge({ visibility }) {
  const config = {
    private: { icon: Lock, label: 'Internal', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    company: { icon: Building2, label: 'Company', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    public: { icon: Globe, label: 'Public', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  };
  const { icon: Icon, label, className } = config[visibility] || config.private;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// Folder tree item
function FolderTreeItem({ folder, selectedFolderId, onSelect, level = 0, expandedFolders, toggleFolder }) {
  const isExpanded = expandedFolders.has(folder.id);
  const isSelected = selectedFolderId === folder.id;
  const hasChildren = folder.children && folder.children.length > 0;

  return (
    <div>
      <button
        onClick={() => onSelect(folder.id)}
        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-sm transition-colors
          ${isSelected
            ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400'
            : 'hover:bg-neutral-100 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300'
          }`}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
      >
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); toggleFolder(folder.id); }}
            className="p-0.5 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded"
          >
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <Folder className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-brand-600 dark:text-brand-400' : 'text-neutral-400'}`} />
        <span className="truncate flex-1">{folder.name}</span>
      </button>
      {isExpanded && hasChildren && (
        <div>
          {folder.children.map(child => (
            <FolderTreeItem
              key={child.id}
              folder={child}
              selectedFolderId={selectedFolderId}
              onSelect={onSelect}
              level={level + 1}
              expandedFolders={expandedFolders}
              toggleFolder={toggleFolder}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Password card
function PasswordCard({ password, isSelected, onSelect, onReveal, isInternal }) {
  const [revealed, setRevealed] = useState({});
  const [copied, setCopied] = useState(null);
  const isExpiring = password.expires_at && new Date(password.expires_at) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  const handleReveal = async (field) => {
    if (revealed[field]) {
      setRevealed(prev => ({ ...prev, [field]: null }));
      return;
    }

    try {
      const response = await onReveal(password.id, field);
      setRevealed(prev => ({ ...prev, [field]: response.data.value }));

      // Auto-hide after 30 seconds
      setTimeout(() => {
        setRevealed(prev => ({ ...prev, [field]: null }));
      }, 30000);
    } catch (error) {
      notifyError('The password could not be revealed.', error);
    }
  };

  const handleCopy = async (value, field) => {
    await navigator.clipboard.writeText(value);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  const typeBadge = passwordTypes[password.password_type] || passwordTypes.other;

  return (
    <div className={`p-4 bg-white dark:bg-neutral-800 rounded-lg border transition-all ${
      isSelected
        ? 'border-brand-500 ring-2 ring-brand-500/20'
        : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600'
    }`}>
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        {isInternal && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect(password.id)}
            className="mt-1 rounded border-neutral-300 dark:border-neutral-600 text-brand-600 focus:ring-brand-500"
          />
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <Link
                to={`/vault/${password.id}`}
                className="font-medium text-neutral-900 dark:text-white hover:text-brand-600 dark:hover:text-brand-400"
              >
                {password.name}
              </Link>
              {password.username_hint && (
                <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                  {password.username_hint}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isExpiring && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                  <AlertTriangle className="w-3 h-3" />
                  Expiring
                </span>
              )}
              {password.password_type && (
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeBadge.className}`}>
                  {typeBadge.label}
                </span>
              )}
              {isInternal && <VisibilityBadge visibility={password.visibility} />}
            </div>
          </div>

          {/* Quick reveal buttons */}
          <div className="flex items-center gap-2 mt-3">
            {/* Username reveal */}
            {password.username_hint && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-neutral-400">User:</span>
                {revealed.username ? (
                  <>
                    <span className="text-sm font-mono text-neutral-700 dark:text-neutral-300">{revealed.username}</span>
                    <button
                      onClick={() => handleCopy(revealed.username, 'username')}
                      className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                    >
                      {copied === 'username' ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                    </button>
                    <button
                      onClick={() => handleReveal('username')}
                      className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                    >
                      <EyeOff className="w-3 h-3" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleReveal('username')}
                    className="text-xs text-brand-600 dark:text-brand-400 hover:underline"
                  >
                    Reveal
                  </button>
                )}
              </div>
            )}

            <span className="text-neutral-300 dark:text-neutral-600">|</span>

            {/* Password reveal */}
            <div className="flex items-center gap-1">
              <span className="text-xs text-neutral-400">Pass:</span>
              {revealed.password ? (
                <>
                  <span className="text-sm font-mono text-neutral-700 dark:text-neutral-300">{revealed.password}</span>
                  <button
                    onClick={() => handleCopy(revealed.password, 'password')}
                    className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                  >
                    {copied === 'password' ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                  </button>
                  <button
                    onClick={() => handleReveal('password')}
                    className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                  >
                    <EyeOff className="w-3 h-3" />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleReveal('password')}
                  className="text-xs text-brand-600 dark:text-brand-400 hover:underline"
                >
                  Reveal
                </button>
              )}
            </div>

            {password.otp_type && (
              <>
                <span className="text-neutral-300 dark:text-neutral-600">|</span>
                <Link
                  to={`/vault/${password.id}`}
                  className="text-xs text-brand-600 dark:text-brand-400 hover:underline"
                >
                  View OTP
                </Link>
              </>
            )}
          </div>

          {/* Meta info */}
          <div className="flex items-center gap-3 mt-2 text-xs text-neutral-500 dark:text-neutral-400">
            {password.folder_name && (
              <span className="flex items-center gap-1">
                <Folder className="w-3 h-3" />
                {password.folder_name}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(password.updated_at).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Bulk actions bar
function BulkActionsBar({ selectedCount, onDelete, onMove, onShare, isDeleting }) {
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 px-4 py-3 bg-neutral-900 dark:bg-neutral-800 text-white rounded-lg shadow-lg z-50">
      <span className="text-sm">{selectedCount} selected</span>
      <div className="w-px h-4 bg-neutral-700" />
      <button
        onClick={onShare}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 rounded text-sm transition-colors"
      >
        <Share2 className="w-4 h-4" />
        Share
      </button>
      <button
        onClick={onMove}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-700 hover:bg-neutral-600 rounded text-sm transition-colors"
      >
        <FolderInput className="w-4 h-4" />
        Move
      </button>
      <button
        onClick={onDelete}
        disabled={isDeleting}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-red-400 rounded text-sm transition-colors"
      >
        <Trash2 className="w-4 h-4" />
        Delete
      </button>
    </div>
  );
}

export default function VaultPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [selectedFolderId, setSelectedFolderId] = useState(searchParams.get('folder') || null);
  const [selectedPasswordType, setSelectedPasswordType] = useState(searchParams.get('type') || '');
  const [showExpiring, setShowExpiring] = useState(searchParams.get('expiring') === 'true');
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [selectedPasswords, setSelectedPasswords] = useState(new Set());
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { selectedCompanyId } = useCompanyStore();

  const isInternal = isInternalRole(user?.role);

  // Fetch folder tree (filtered by company for multi-company users)
  const { data: folderData } = useQuery({
    queryKey: ['documentation', 'folders', 'tree', selectedCompanyId],
    queryFn: () => documentationAPI.getFolderTree({ companyId: selectedCompanyId }),
  });

  // Fetch passwords for selected company
  const debouncedSearch = useDebounce(search, 300);
  // Note: Vaults are kept separate per company - not combined
  const { data: passwordsData, isLoading } = useQuery({
    queryKey: ['vault', 'passwords', { companyId: selectedCompanyId, folderId: selectedFolderId, search: debouncedSearch, passwordType: selectedPasswordType, expiring: showExpiring }],
    queryFn: () => vaultAPI.listPasswords({
      companyId: selectedCompanyId,
      folderId: selectedFolderId,
      search: debouncedSearch || undefined,
      passwordType: selectedPasswordType || undefined,
      expiringWithinDays: showExpiring ? 30 : undefined,
      limit: 50
    }),
  });

  const folders = folderData?.data?.tree || [];
  const passwords = passwordsData?.data?.passwords || [];
  const pagination = passwordsData?.data?.pagination;

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedFolderId) params.set('folder', selectedFolderId);
    if (search) params.set('q', search);
    if (selectedPasswordType) params.set('type', selectedPasswordType);
    if (showExpiring) params.set('expiring', 'true');
    setSearchParams(params, { replace: true });
  }, [selectedFolderId, search, selectedPasswordType, showExpiring, setSearchParams]);

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const togglePasswordSelection = (passwordId) => {
    setSelectedPasswords(prev => {
      const next = new Set(prev);
      if (next.has(passwordId)) {
        next.delete(passwordId);
      } else {
        next.add(passwordId);
      }
      return next;
    });
  };

  const selectAll = () => {
    if (selectedPasswords.size === passwords.length) {
      setSelectedPasswords(new Set());
    } else {
      setSelectedPasswords(new Set(passwords.map(p => p.id)));
    }
  };

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: (ids) => vaultAPI.bulkDeletePasswords(Array.from(ids)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vault', 'passwords'] });
      setSelectedPasswords(new Set());
    },
  });

  const handleBulkDelete = () => {
    if (confirm(`Delete ${selectedPasswords.size} passwords? This cannot be undone.`)) {
      bulkDeleteMutation.mutate(selectedPasswords);
    }
  };

  const handleReveal = (passwordId, field) => {
    return vaultAPI.revealField(passwordId, field);
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Password Vault</h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            {isInternal ? 'Manage encrypted credentials' : 'View shared credentials'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Company filter - vaults are separate per company */}
          <CompanyFilter />
          {isInternal && (
            <Link
              to="/vault/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Password
            </Link>
          )}
        </div>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search passwords..."
            className="w-full pl-10 pr-4 py-2.5 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={selectedPasswordType}
            onChange={(e) => setSelectedPasswordType(e.target.value)}
            className="px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          >
            <option value="">All Types</option>
            {Object.entries(passwordTypes).map(([value, { label }]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <button
            onClick={() => setShowExpiring(!showExpiring)}
            className={`flex items-center gap-2 px-3 py-2 border rounded-lg transition-colors ${
              showExpiring
                ? 'border-orange-500 bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400'
                : 'border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700'
            }`}
          >
            <AlertTriangle className="w-4 h-4" />
            Expiring Soon
          </button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Sidebar - Folder Tree */}
        <div className="hidden lg:block w-64 flex-shrink-0">
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
            <h3 className="font-medium text-neutral-900 dark:text-white mb-3">Folders</h3>
            <button
              onClick={() => setSelectedFolderId(null)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-sm transition-colors mb-2
                ${selectedFolderId === null
                  ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400'
                  : 'hover:bg-neutral-100 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300'
                }`}
            >
              <Key className="w-4 h-4" />
              All Passwords
            </button>
            <div className="space-y-0.5">
              {folders.map(folder => (
                <FolderTreeItem
                  key={folder.id}
                  folder={folder}
                  selectedFolderId={selectedFolderId}
                  onSelect={setSelectedFolderId}
                  expandedFolders={expandedFolders}
                  toggleFolder={toggleFolder}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Main content - Password list */}
        <div className="flex-1 min-w-0">
          {/* Select all header */}
          {isInternal && passwords.length > 0 && (
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-neutral-200 dark:border-neutral-700">
              <input
                type="checkbox"
                checked={selectedPasswords.size === passwords.length && passwords.length > 0}
                onChange={selectAll}
                className="rounded border-neutral-300 dark:border-neutral-600 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-neutral-600 dark:text-neutral-400">
                {selectedPasswords.size > 0 ? `${selectedPasswords.size} selected` : 'Select all'}
              </span>
            </div>
          )}

          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="p-4 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 animate-pulse">
                  <div className="h-5 bg-neutral-200 dark:bg-neutral-700 rounded w-1/3 mb-2" />
                  <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/4" />
                </div>
              ))}
            </div>
          ) : passwords.length > 0 ? (
            <>
              <div className="space-y-3">
                {passwords.map(password => (
                  <PasswordCard
                    key={password.id}
                    password={password}
                    isSelected={selectedPasswords.has(password.id)}
                    onSelect={togglePasswordSelection}
                    onReveal={handleReveal}
                    isInternal={isInternal}
                  />
                ))}
              </div>
              {pagination && pagination.pages > 1 && (
                <div className="mt-6 flex justify-center">
                  <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                    Page {pagination.page} of {pagination.pages} ({pagination.total} passwords)
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
              <Key className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
              <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
                {search ? 'No passwords found' : 'No passwords yet'}
              </h3>
              <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                {search
                  ? 'Try adjusting your search terms'
                  : isInternal
                    ? 'Create your first password to get started'
                    : 'No passwords have been shared with you yet'
                }
              </p>
              {isInternal && !search && (
                <Link
                  to="/vault/new"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  New Password
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedPasswords.size > 0 && (
        <BulkActionsBar
          selectedCount={selectedPasswords.size}
          onDelete={handleBulkDelete}
          onMove={() => {/* TODO: Open move modal */}}
          onShare={() => {/* TODO: Open share modal */}}
          isDeleting={bulkDeleteMutation.isPending}
        />
      )}
    </div>
  );
}
